import { useQuery } from '@tanstack/react-query';
import {
  getConversationsUnreadCount,
  getUserConversationUnreadCount,
} from '../api/conversations.api';
import { queryKeys } from '../api/queryKeys';
import {
  ConversationUnreadCountResponse,
  ConversationUserUnreadCountResponse,
} from '../types/conversation.types';

/**
 * Unread badge.
 *
 * Modelled on `useVerificationsCount` but with **no `refetchInterval`** - the
 * socket pushes every change, so polling would be pure waste. The only other
 * writers are `POST .../read` and `read-all`, which both answer with the
 * authoritative counts.
 *
 * `GET /v1/conversations/unread-count` returns a different shape per app, so
 * this is two hooks rather than one with a union return type - a union would
 * leak into every badge that consumes it. They share a cache key because a
 * given bundle only ever calls one of them.
 */

const ZERO_ADMIN: ConversationUnreadCountResponse = { threads: 0, messages: 0 };
const ZERO_USER: ConversationUserUnreadCountResponse = { unread: 0 };

/**
 * Admin side: thread and message totals across every conversation, and the
 * same per inbox tab - see `ConversationUnreadCountResponse`.
 */
export function useChatUnreadCount({ enabled = true } = {}) {
  const query = useQuery({
    queryKey: queryKeys.conversations.unreadCount,
    queryFn: async () => {
      const { data } = await getConversationsUnreadCount();
      return data.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });

  return {
    count: query.data ?? ZERO_ADMIN,
    isLoading: query.isLoading,
  };
}

/**
 * The three numbers the merchant's chat shows, from either generation of the
 * response.
 *
 * An API from before merchant groups sends `unread` alone, which is the support
 * thread - so with nothing else to go on there are no groups and the total IS
 * the support count. Read through here rather than field by field, so that
 * fallback is written once and no badge can show `undefined`.
 */
export const userUnreadTotals = (
  count: ConversationUserUnreadCountResponse
) => {
  const support = count.support ?? count.unread;
  const groups = count.groups ?? 0;
  return { support, groups, total: count.total ?? support + groups };
};

/**
 * Merchant side: the support thread and the merchant groups, apart and
 * together - see `userUnreadTotals` for reading it.
 *
 * The per-location breakdown the old endpoint returned is gone with the
 * location model - an outside user now has a single support conversation with
 * vGrubs no matter how many locations they belong to.
 */
export function useUserConversationUnreadCount({ enabled = true } = {}) {
  const query = useQuery({
    queryKey: queryKeys.conversations.unreadCount,
    queryFn: async () => {
      const { data } = await getUserConversationUnreadCount();
      return data.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });

  const count = query.data ?? ZERO_USER;
  return {
    count,
    ...userUnreadTotals(count),
    isLoading: query.isLoading,
  };
}
